import { Autocomplete, FormControl, FormHelperText, TextField } from '@mui/material';
import { forwardRef, ForwardRefExoticComponent, PropsWithoutRef, RefAttributes, useEffect } from 'react';
import { Controller, useFormContext as useReactHookFormContext } from 'react-hook-form';
import { useSiloRealFormContext } from '../base/use-form-context';
import { useFormFieldStyles } from './use-form-field-styles';

type Option = string | { id: string; name: string };

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

type Props = {
  tid: string;
  name: string;
  required?: boolean;
  label?: string;
  size?: 'medium' | 'large';
  options: Option[];
  // renderOption?: (option: Option) => React.ReactNode;
  onChange?: (value: string | null) => void;
};

const getOptionId = (option: Option) => (typeof option === 'string' ? option : option.id);
const getOptionLabel = (option: Option) => (typeof option === 'string' ? option : option.name);

export const combobox: FR<HTMLInputElement, Props> = forwardRef((props, ref) => {
  const { name = 'combobox', label = 'Seleccionar', required = false } = props;
  const { options = [], onChange, size } = props;
  // const { renderOption } = props;

  const { verify } = useSiloRealFormContext();
  useEffect(() => verify.uniqueness(name), []);

  const {
    control,
    formState: { errors },
  } = useReactHookFormContext();
  const hasError = Boolean(errors[name]);
  const errorMessage = errors[name]?.message as string | undefined;

  const fieldSx = useFormFieldStyles({ size });

  return (
    <FormControl sx={fieldSx} error={hasError} required={required}>
      <Controller
        name={name}
        control={control}
        defaultValue={''}
        render={({ field }) => {
          const current = options.find((o) => getOptionId(o) === field.value) ?? null;

          return (
            <Autocomplete
              value={current}
              options={options}
              getOptionLabel={(opt) => getOptionLabel(opt as Option)}
              isOptionEqualToValue={(opt, val) => getOptionId(opt as Option) === getOptionId(val as Option)}
              onChange={(_, newValue) => {
                const next = newValue ? getOptionId(newValue as Option) : '';
                field.onChange(next);
                onChange?.(next || null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={ref}
                  label={label}
                  size="small"
                  data-testid={props.tid}
                  sx={{ backgroundColor: '#fff' }}
                />
              )}
              // renderOption={renderOption as any}
            />
          );
        }}
      />
      <FormHelperText>{errorMessage}</FormHelperText>
    </FormControl>
  );
});

combobox.displayName = 'SiloReal-Form-Field-Combobox';
